/**
 * EMAIL FORM MODAL — Modale di registrazione email.
 *
 * Aperto da PersonDetail/BusinessDetail (azioni "+ Nuova email" o
 * dalla timeline tab 'email').
 *
 * Campi (schema yaml `EmailInput`):
 *  - direction (IN/OUT, required)
 *  - emailDate (datetime, required) — di default "now"
 *  - description (string, required)
 *
 * Differenza da Call: niente `status` (Email backend non traccia esito).
 *
 * Al submit:
 *  1. dispatch createEmail(payload) -> Email
 *  2. createBidirectionalRelationship Email<->record per ogni associato
 *  3. se linkedEntity presente (aperto da Detail), anche quello collegato
 */

import { useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { datetimeLocalToIso, nowDatetimeLocal, htmlHasText } from '@crm/shared';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useToast } from '../../components/useToast';
import { useAppDispatch } from '../../store/hooks';
import { createEmail } from './emailThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { AssociatedRecordsField } from '../../components/AssociatedRecordsField';
import { ContactedPeopleField } from '../../components/ContactedPeopleField';
import { EMAIL_DIRECTIONS, type Email, type EmailCreatePayload, type EmailDirection } from './emailTypes';

interface EmailFormModalProps {
  onClose: () => void;
  onCreated?: () => void;
  /** Direzione di default quando il modal viene aperto da un'azione
   *  specifica (es. tasto "+ Email" da Detail pre-imposta OUT). */
  initialDirection?: EmailDirection;
  /** Se presente, dopo la create dispatcha una Relationship bidirezionale
   *  Email<->linkedEntity (es. Person/Business corrente). */
  linkedEntity?: { className: string; classPK: number };
}

const FORM_ID = 'email-form';

function defaultForm(initialDirection?: EmailDirection): EmailCreatePayload {
  return {
    direction: initialDirection ?? 'OUT',
    emailDate: datetimeLocalToIso(nowDatetimeLocal()) ?? '',
    description: '',
  };
}

export default function EmailFormModal({ onClose, onCreated, initialDirection, linkedEntity }: EmailFormModalProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<EmailCreatePayload>(() => defaultForm(initialDirection));
  // Stato datetime-local separato (formato "YYYY-MM-DDTHH:mm"); al submit
  // viene convertito in ISO "...:00Z" coerente con emailDate atteso.
  const [emailDateLocal, setEmailDateLocal] = useState<string>(() => nowDatetimeLocal());
  // Rimonta il RichTextEditor (non controllato) per svuotarlo dopo un submit "keep".
  const [editorKey, setEditorKey] = useState(0);

  const emailFqn = getEntityFqn('Email');

  const { records, setRecords, applyAtSubmit, pickerProps } = useAssociatedRecordsBinder({
    mode: 'create',
    entityFqn: emailFqn ?? undefined,
    reference: linkedEntity ?? null,
  });

  const submitIntentRef = useRef<'close' | 'keep'>('close');

  const canSubmit = htmlHasText(form.description) && emailDateLocal.length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const intent = submitIntentRef.current;
    submitIntentRef.current = 'close';

    const payload: EmailCreatePayload = {
      ...form,
      emailDate: datetimeLocalToIso(emailDateLocal) ?? '',
    };

    setSubmitting(true);
    try {
      let created: Email;
      try {
        created = (await dispatch(createEmail(payload)).unwrap()) as Email;
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        return;
      }
      if (!emailFqn) {
        console.warn(
          '[relationship] EmailFormModal: Email non registrato nel registry — link saltati',
          { selectedRecords: records.length },
        );
      } else {
        await applyAtSubmit?.(emailFqn, created.emailId);
      }
      toast({ severity: 'success', message: t('common.saved') });
      onCreated?.();
      if (intent === 'keep') {
        setForm(defaultForm(initialDirection));
        setEmailDateLocal(nowDatetimeLocal());
        setEditorKey((k) => k + 1);
        setRecords?.([]);
      } else {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <>
      <Button
        type="submit"
        form={FORM_ID}
        onClick={() => { submitIntentRef.current = 'close'; }}
        variant="contained"
        disabled={submitting || !canSubmit}
      >
        {t('common.create')}
      </Button>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
    </>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{t('email.createTitle')}</span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <form id={FORM_ID} onSubmit={handleSubmit} className="email-form">
          {/* Riga 1: Contattati | Direzione | Data (3 col).
              Contattati è una vista Person-filtered di `records`, sincronizzata
              col widget associazioni (stesso setRecords, merge dei non-Person). */}
          <div className="task-form__grid task-form__grid--3col">
            <ContactedPeopleField
              value={records.filter((r) => r.typeName === 'Person')}
              onChange={(people) =>
                setRecords?.([...records.filter((r) => r.typeName !== 'Person'), ...people])
              }
            />
            <TextField
              select
              label={t('email.direction')}
              value={form.direction}
              onChange={(e) => setForm({ ...form, direction: e.target.value as EmailDirection })}
              required
              fullWidth
              className="form-field"
            >
              {EMAIL_DIRECTIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>{t(d.label)}</MenuItem>
              ))}
            </TextField>
            <TextField
              type="datetime-local"
              label={t('email.emailDate')}
              value={emailDateLocal}
              onChange={(e) => setEmailDateLocal(e.target.value)}
              required
              fullWidth
              className="form-field"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </div>

          {/* Descrizione full-width WYSIWYG */}
          <div className="form-field">
            <label className="field-row__label text-xs text-muted">{t('email.description')}</label>
            <RichTextEditor
              key={editorKey}
              onChange={(html) => setForm({ ...form, description: html })}
              placeholder={t('email.descriptionPlaceholder')}
            />
          </div>

          {/* Associa ai record (polimorfico) */}
          <AssociatedRecordsField
            {...pickerProps}
            records={records}
            onChange={(val) => setRecords?.(val)}
            displayMode="click-to-edit"
          />
        </form>
      </DialogContent>
      <DialogActions className="app-dialog__footer">{footer}</DialogActions>
    </Dialog>
  );
}
